import React from 'react';
import Button from '../../../../components/Button/index';
import CustomModal from '../../../../components/Modal/CustomModal';
import { MissingField } from './usePublishArticle';

// Icon components for missing fields
const TitleIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
  </svg>
);

const ImageIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

const ContentIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

const getFieldIcon = (field: string) => {
  switch (field) {
    case 'title':
      return <TitleIcon />;
    case 'cover':
      return <ImageIcon />;
    case 'content':
      return <ContentIcon />;
    default:
      return <TitleIcon />;
  }
};

interface UseArticleEditorModalsProps {
  setModal: (modal: string) => void;
  step: number;
  setStep: (step: number) => void;
  loading: { publish: boolean; saveDraft: boolean };
  handlePublish: (onClose?: () => void) => void;
  forceSave: () => void;
  triggerSave: () => void;
  isEdit: boolean;
  missingFields?: MissingField[];
}

export const useArticleEditorModals = ({
  setModal,
  // step,
  // setStep,
  loading,
  handlePublish,
  // forceSave,
  // triggerSave,
  isEdit,
  missingFields = []
}: UseArticleEditorModalsProps) => {
  const renderPublishModal = (onClose) => (
    <CustomModal
      isOpen
      title={isEdit ? 'Save Article?' : 'Publish New Article?'}
      onClick={onClose}
      hideFooter
      maxW="400px"
    >
      <div className="w-full">
        <p className="text-lg">
          {' '}
          {isEdit
            ? 'Save changes to update your content'
            : 'This will share your article to the public'}
        </p>
        <div className="flex justify-end gap-[10px] mt-6">
          <Button variant="outline" onClick={onClose}>
            Keep editing
          </Button>
          <Button
            onClick={() => handlePublish(onClose)}
            isLoading={loading.publish}
            disabled={loading.publish}
          >
            {isEdit ? 'Save' : 'Publish'}
          </Button>
        </div>
      </div>
    </CustomModal>
  );

  const renderPublishNoMetadataErrorModal = (onClose) => (
    <CustomModal isOpen title="Almost There!" onClick={onClose} hideFooter maxW="480px">
      <div className="w-full flex flex-col">
        {/* Header Section */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <svg
              className="w-6 h-6 text-amber-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div>
            <p className="text-lg font-bold text-gray-900">Complete these details to publish</p>
            <p className="text-sm text-gray-500">
              Your article needs a few more things before it's ready
            </p>
          </div>
        </div>

        {/* Missing Fields List */}
        <div className="bg-gray-50 rounded-xl p-4 mb-6">
          <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            Required fields
          </p>
          <div className="space-y-3">
            {missingFields.map((field, index) => (
              <div
                key={field.field}
                className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-200 transition-all hover:border-amber-300 hover:shadow-sm"
              >
                <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0 text-amber-600">
                  {getFieldIcon(field.field)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm">{field.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{field.description}</p>
                </div>
                <div className="flex-shrink-0">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                    Missing
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 w-full">
          <Button
            className="flex-1"
            onClick={() => {
              onClose();
              // Scroll to the post details section if title or cover is missing
              setTimeout(() => {
                const metadataSection = document.getElementById('article-metadata-header');
                if (metadataSection) {
                  metadataSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 100);
            }}
          >
            Continue editing
          </Button>
        </div>
      </div>
    </CustomModal>
  );

  const renderPublishNoContentErrorModal = (onClose) => (
    <CustomModal isOpen title="Ready to Write?" onClick={onClose} hideFooter maxW="480px">
      <div className="w-full flex flex-col">
        {/* Header Section */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
            <svg
              className="w-6 h-6 text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
              />
            </svg>
          </div>
          <div>
            <p className="text-lg font-bold text-gray-900">Your article needs content</p>
            <p className="text-sm text-gray-500">Start writing to bring your ideas to life</p>
          </div>
        </div>

        {/* Missing Content Info */}
        <div className="bg-blue-50 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0 text-blue-600">
              <ContentIcon />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-blue-900 text-sm">Article Content</p>
              <p className="text-xs text-blue-700 mt-1">
                Share your thoughts, insights, and stories with your readers. The editor is ready
                for your first paragraph.
              </p>
            </div>
          </div>
        </div>

        {/* Helpful Tips */}
        <div className="mb-6">
          <p className="text-sm font-semibold text-gray-700 mb-2">Quick tips to get started:</p>
          <ul className="text-sm text-gray-600 space-y-1.5">
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
              Start with a strong opening that hooks your readers
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
              Use headers to organize your ideas
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
              Add images and media to enhance your story
            </li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 w-full">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="flex-1"
            onClick={onClose}
            isLoading={loading.publish}
            disabled={loading.publish}
          >
            Start writing
          </Button>
        </div>
      </div>
    </CustomModal>
  );

  return {
    renderPublishModal,
    renderPublishNoMetadataErrorModal,
    renderPublishNoContentErrorModal
  };
};
